import React, { createContext, useContext, useMemo } from 'react';
import {
  SortDirection,
  TableViewType,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  ColumnTypes,
  normalizeColumnTypes,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/column-types';

export interface DefaultViewHint {
  breakdown: string | null;
  columnOrder: string[] | null;
  columnTypes: ColumnTypes | null;
  columnWidths: Record<string, number> | null;
  sort: { column: string; direction: SortDirection } | null;
  tooltips: Record<string, string> | null;
  type: TableViewType | null;
  xAxis: string | null;
  yAxis: string | null;
}

const DEFAULT_VIEW_HINT_FENCE_REGEX =
  /```vsly\/json\/default_view\n([\s\S]*?)```/;

const MARKDOWN_TABLE_SEPARATOR_ROW_REGEX =
  /^\s*\|?(?:\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?\s*$/m;

const HINT_VIEW_TYPE_ALIASES: Record<string, TableViewType> = {
  bar: `bar`,
  column: `bar`,
  line: `line`,
  pie: `pie`,
  table: `table`,
};

/**
 * Extracts the AI-generated default visualization hint from a chat message.
 * The agent emits a `vsly/json/default_view` fenced code block suggesting
 * which view and axes best fit the table it follows.
 */
export function parseDefaultViewHint(markdown: string): DefaultViewHint | null {
  const match = markdown.match(DEFAULT_VIEW_HINT_FENCE_REGEX);

  if (!match) {
    return null;
  }

  try {
    const parsed = JSON.parse(match[1].trim());
    const hint: DefaultViewHint = {
      breakdown: normalizeHintAxisLabel(parsed?.breakdown),
      columnOrder: normalizeHintColumnOrder(parsed?.[`column_order`]),
      columnTypes: normalizeColumnTypes(parsed?.[`column_types`]),
      columnWidths: normalizeHintColumnWidths(parsed?.[`column_widths`]),
      sort: normalizeHintSort(parsed?.sort),
      tooltips: normalizeHintTooltips(parsed?.tooltips),
      type: normalizeHintViewType(parsed?.type),
      xAxis: normalizeHintAxisLabel(parsed?.[`x-axis`]),
      yAxis: normalizeHintAxisLabel(parsed?.[`y-axis`]),
    };

    return hint.type ||
      hint.xAxis ||
      hint.yAxis ||
      hint.breakdown ||
      hint.tooltips ||
      hint.sort ||
      hint.columnOrder ||
      hint.columnWidths ||
      hint.columnTypes
      ? hint
      : null;
  } catch {
    return null;
  }
}

/**
 * Checks whether a chat message contains a `vsly/json/default_view` fenced
 * hint block, which the agent emits alongside a pinnable data table.
 */
export function hasDefaultViewBlock(markdown: string): boolean {
  return DEFAULT_VIEW_HINT_FENCE_REGEX.test(markdown ?? ``);
}

/**
 * Checks whether a chat message contains a markdown data table
 * (a header row followed by a `|---|` separator row).
 */
export function hasMarkdownDataTable(markdown: string): boolean {
  return MARKDOWN_TABLE_SEPARATOR_ROW_REGEX.test(markdown ?? ``);
}

/**
 * A message can be pinned into a reusable report only when it carries both
 * the AI-generated default view hint and an actual data table to back it.
 */
export function isPinnableMessage(markdown: string): boolean {
  return hasDefaultViewBlock(markdown) && hasMarkdownDataTable(markdown);
}

function normalizeHintViewType(rawType: unknown): TableViewType | null {
  if (typeof rawType !== `string`) {
    return null;
  }

  return HINT_VIEW_TYPE_ALIASES[rawType.trim().toLowerCase()] ?? null;
}

function normalizeHintAxisLabel(rawAxis: unknown): string | null {
  return typeof rawAxis === `string` && rawAxis.trim() ? rawAxis.trim() : null;
}

function normalizeHintSort(
  rawSort: unknown,
): { column: string; direction: SortDirection } | null {
  if (typeof rawSort !== `object` || rawSort === null) {
    return null;
  }
  const { column, direction } = rawSort as Record<string, unknown>;
  const isValidDirection =
    direction === `ascending` || direction === `descending`;

  return typeof column === `string` && column.trim() && isValidDirection
    ? { column: column.trim(), direction }
    : null;
}

function normalizeHintColumnOrder(rawColumnOrder: unknown): string[] | null {
  if (!Array.isArray(rawColumnOrder)) {
    return null;
  }
  const columnOrder = rawColumnOrder.filter(
    (column): column is string =>
      typeof column === `string` && column.trim().length > 0,
  );

  return columnOrder.length > 0 ? columnOrder : null;
}

function normalizeHintColumnWidths(
  rawColumnWidths: unknown,
): Record<string, number> | null {
  if (typeof rawColumnWidths !== `object` || rawColumnWidths === null) {
    return null;
  }

  const columnWidths = Object.entries(
    rawColumnWidths as Record<string, unknown>,
  ).reduce<Record<string, number>>((normalized, [column, width]) => {
    if (column.trim() && typeof width === `number` && width > 0) {
      normalized[column.trim()] = width;
    }
    return normalized;
  }, {});

  return Object.keys(columnWidths).length > 0 ? columnWidths : null;
}

function normalizeHintTooltips(
  rawTooltips: unknown,
): Record<string, string> | null {
  if (typeof rawTooltips !== `object` || rawTooltips === null) {
    return null;
  }

  const tooltips = Object.entries(
    rawTooltips as Record<string, unknown>,
  ).reduce<Record<string, string>>((normalized, [label, description]) => {
    if (
      typeof label === `string` &&
      label.trim() &&
      typeof description === `string` &&
      description.trim()
    ) {
      normalized[label.trim()] = description.trim();
    }

    return normalized;
  }, {});

  return Object.keys(tooltips).length > 0 ? tooltips : null;
}

const DefaultViewHintContext = createContext<DefaultViewHint | null>(null);

export function DefaultViewHintProvider({
  children,
  markdown,
}: {
  children?: React.ReactNode;
  markdown: string;
}) {
  const hint = useMemo(() => parseDefaultViewHint(markdown), [markdown]);

  return (
    <DefaultViewHintContext.Provider value={hint}>
      {children}
    </DefaultViewHintContext.Provider>
  );
}

export function useDefaultViewHint() {
  return useContext(DefaultViewHintContext);
}
